<template>
    <component
        :is="triggerTag"
        ref="triggerRef"
        v-bind="triggerAttrs"
        :type="triggerTag === 'button' ? 'button' : undefined"
        :aria-label="label || title || undefined"
        :aria-controls="panelId"
        :aria-disabled="disabled || undefined"
        :disabled="triggerTag === 'button' && disabled"
        :class="[
            'cell-popover-trigger',
            triggerClass,
            disabled && 'cell-popover-trigger--disabled'
        ]"
        :tabindex="disabled ? -1 : 0"
        role="button"
        :aria-expanded="open"
        aria-haspopup="dialog"
        @click.stop="onTriggerClick"
        @mouseenter="onHoverEnter"
        @mouseleave="onHoverLeave"
        @keydown.enter.prevent="onTriggerClick"
        @keydown.space.prevent="onTriggerClick"
    >
        <slot name="trigger" :open="open" />
    </component>

    <teleport to="body" :disabled="!open">
        <template v-if="open || keepMounted">
            <div
                v-if="open && isMobile"
                class="cell-popover__backdrop"
                @mousedown.prevent
                @click="ownership?.isTop() && close()"
            ></div>

            <div
                ref="panelRef"
                v-show="open"
                :id="panelId"
                tabindex="-1"
                :aria-label="title || label || 'Details'"
                :aria-modal="isMobile ? true : undefined"
                :aria-hidden="!open || undefined"
                :class="[
                    'cell-popover__panel',
                    isMobile ? 'cell-popover__panel--sheet' : 'cell-popover__panel--anchored'
                ]"
                :style="panelStyle"
                role="dialog"
                @click.capture="onActionClick"
                @mouseenter="onHoverEnter"
                @mouseleave="onHoverLeave"
            >
                <div v-if="isMobile && title" class="cell-popover__sheet-header">
                    <strong>{{ title }}</strong>
                    <button
                        type="button"
                        class="btn-close"
                        aria-label="Close"
                        @click="close"
                    ></button>
                </div>
                <div class="cell-popover__body">
                    <slot :close="close" />
                </div>
            </div>
        </template>
    </teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick, useId } from "vue";
import { ownPopover, popoverFocusable } from "@/composables/popoverFocus";
import { useStore } from "vuex";

const props = defineProps({
    disabled: { type: Boolean, default: false },
    triggerTag: { type: String, default: "span" },
    triggerClass: { type: [String, Array, Object], default: "" },
    triggerAttrs: { type: Object, default: () => ({}) },
    label: { type: String, default: "" },
    keepMounted: { type: Boolean, default: false },
    actionMenu: { type: Boolean, default: false },
    title: { type: String, default: "" },
    minWidth: { type: Number, default: 260 },
    maxWidth: { type: Number, default: 360 },
    // "start" anchors the panel's left edge to the trigger; "end" anchors its
    // right edge (drops left) — use for triggers pinned to the right edge.
    align: { type: String, default: "start" },
    openOnHover: { type: Boolean, default: false },
    hoverCloseDelay: { type: Number, default: 140 }
});

const emit = defineEmits(["open", "close"]);

const store = useStore();
const isMobile = computed(() => store.state.isMobile);

const panelId = `popover-${useId()}`;
let ownership = null;
const open = ref(false);
const triggerRef = ref(null);
const panelRef = ref(null);
const panelStyle = ref({});
let hoverCloseTimer = null;

const clearHoverCloseTimer = () => {
    if (!hoverCloseTimer) return;
    window.clearTimeout(hoverCloseTimer);
    hoverCloseTimer = null;
};

const computePosition = () => {
    if (isMobile.value) {
        panelStyle.value = {};
        return;
    }
    if (!triggerRef.value) return;
    const rect = triggerRef.value.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const gutter = 8;

    const width = Math.min(props.maxWidth, Math.max(props.minWidth, rect.width));
    let left = props.align === "end" ? rect.right - width : rect.left;
    if (left + width + gutter > vw) left = vw - width - gutter;
    if (left < gutter) left = gutter;

    let top = rect.bottom + 4;
    // Flip above if not enough room below
    const estimatedHeight = panelRef.value?.offsetHeight || 240;
    if (top + estimatedHeight + gutter > vh && rect.top - estimatedHeight - gutter > 0) {
        top = rect.top - estimatedHeight - 4;
    }

    panelStyle.value = {
        top: `${Math.round(top)}px`,
        left: `${Math.round(left)}px`,
        width: `${Math.round(width)}px`
    };
};

const onDocumentClick = (event) => {
    if (!open.value || !ownership?.isTop()) return;
    // The sheet backdrop closes on click. Closing on mousedown lets the
    // browser's default pointer focus overwrite the restored launcher focus.
    if (isMobile.value) return;
    if (panelRef.value?.contains(event.target)) return;
    if (triggerRef.value?.contains(event.target)) return;
    close();
};

const focusPanel = () => panelRef.value?.focus({ preventScroll: true });

const onKeydown = (event) => {
    if (!open.value || !ownership?.isTop()) return;
    if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        close();
    } else if (event.key === "Tab" && isMobile.value) {
        const items = popoverFocusable(panelRef.value);
        const index = items.indexOf(document.activeElement);
        if (!items.length) {
            event.preventDefault();
            panelRef.value?.focus();
        } else if (event.shiftKey && index <= 0) {
            event.preventDefault();
            items.at(-1).focus();
        } else if (!event.shiftKey && (index < 0 || index === items.length - 1)) {
            event.preventDefault();
            items[0].focus();
        }
    } else if (event.key === "Tab" && !isMobile.value) {
        const items = popoverFocusable(panelRef.value);
        if (
            (event.shiftKey && document.activeElement === items[0]) ||
            (!event.shiftKey && document.activeElement === items.at(-1))
        )
            close();
    }
};

const onFocusIn = (event) => {
    if (
        open.value &&
        isMobile.value &&
        ownership?.isTop() &&
        !panelRef.value?.contains(event.target)
    )
        focusPanel();
};

const onActionClick = (event) => {
    if (!props.actionMenu) return;
    const action = event.target.closest("button, a[href]");
    if (!action || action.disabled || action.getAttribute("aria-disabled") === "true") return;
    // Release the modal surface and restore the launcher before the action's
    // handler captures focus or opens its next dialog. Vue patches after this event.
    close();
};

const onScrollOrResize = () => {
    if (!open.value) return;
    if (isMobile.value) return;
    computePosition();
};

const onTriggerClick = () => {
    if (props.disabled) return;
    open.value ? close() : openPanel();
};

const openPanel = async () => {
    clearHoverCloseTimer();
    open.value = true;
    emit("open");
    await nextTick();
    computePosition();
    if (!open.value || !panelRef.value) return;
    ownership = ownPopover(panelRef.value, isMobile.value);
    computePosition();
    focusPanel();
};

const close = () => {
    clearHoverCloseTimer();
    if (!open.value) return;
    open.value = false;
    ownership?.release();
    ownership = null;
    triggerRef.value?.focus({ preventScroll: true });
    emit("close");
};

const onHoverEnter = () => {
    if (!props.openOnHover || props.disabled || isMobile.value) return;
    clearHoverCloseTimer();
    if (!open.value) openPanel();
};

const onHoverLeave = () => {
    if (!props.openOnHover || isMobile.value) return;
    clearHoverCloseTimer();
    hoverCloseTimer = window.setTimeout(close, props.hoverCloseDelay);
};

watch(isMobile, async () => {
    if (!open.value) return;
    ownership?.release();
    await nextTick();
    if (!open.value) return;
    ownership = ownPopover(panelRef.value, isMobile.value);
    computePosition();
    focusPanel();
});

onMounted(() => {
    document.addEventListener("mousedown", onDocumentClick);
    document.addEventListener("keydown", onKeydown, true);
    document.addEventListener("focusin", onFocusIn);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
});

onBeforeUnmount(() => {
    clearHoverCloseTimer();
    ownership?.release();
    document.removeEventListener("mousedown", onDocumentClick);
    document.removeEventListener("keydown", onKeydown, true);
    document.removeEventListener("focusin", onFocusIn);
    window.removeEventListener("scroll", onScrollOrResize, true);
    window.removeEventListener("resize", onScrollOrResize);
});

defineExpose({
    open: openPanel,
    close,
    focus: () => triggerRef.value?.focus({ preventScroll: true })
});
</script>

<style scoped>
.cell-popover-trigger {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.25rem;
    padding: 0.15rem 0.35rem;
    border-radius: 0.375rem;
    cursor: pointer;
    min-height: 30px;
    width: 100%;
    transition: background-color 0.12s ease;
}

.cell-popover-trigger:hover,
.cell-popover-trigger:focus-visible {
    background: var(--bs-tertiary-bg, #f1f3f5);
}

.cell-popover-trigger:focus-visible {
    outline: 3px solid var(--forkto-focus-ring);
    outline-offset: 2px;
}

.cell-popover-trigger--disabled {
    cursor: default;
    pointer-events: none;
    opacity: 0.7;
}
</style>

<style>
.cell-popover__panel {
    z-index: 1080;
    background: var(--forkto-surface);
    border: 1px solid var(--forkto-line);
    border-radius: 0.5rem;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    overflow: hidden;
}

.cell-popover__panel--anchored {
    position: fixed;
}

.cell-popover__panel--sheet {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 1rem 1rem 0 0;
    max-height: 80vh;
    max-height: 80dvh;
    display: flex;
    flex-direction: column;
    padding-bottom: var(--forkto-safe-area-bottom, 0px);
}

.cell-popover__sheet-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--forkto-line);
}

.cell-popover__sheet-header {
    flex: 0 0 auto;
    gap: 0.75rem;
}

.cell-popover__sheet-header strong {
    min-width: 0;
    overflow-wrap: anywhere;
}

.cell-popover__sheet-header .btn-close {
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
    box-sizing: border-box;
    padding: 0;
}

.cell-popover__panel :focus-visible {
    outline: 3px solid var(--forkto-focus-ring);
    outline-offset: -3px;
    box-shadow: none;
}

.cell-popover__body {
    min-height: 0;
    overscroll-behavior: contain;
    padding: 0.75rem;
    overflow-y: auto;
    max-height: inherit;
}

.cell-popover__backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 1075;
}
</style>
